import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import {
  CheckCircle, XCircle, Clock, TrendingUp, BarChart3, UserCheck,
  Eye, Upload, FileText, Loader2, ExternalLink, Search, Edit,
  Trash2, Car, Bike, Zap, Truck, RefreshCw, Shield, Plus, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import AdminUnassignedVerifications from './AdminUnassignedVerifications';
import BadeSahabData from './Badesahabdata';
import AdminVerificationReview from './Adminverificationreview';
import BlogsManager from './Blogsmanager';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ══ Insurance Companies Manager ══════════════════════════════════════════════
const InsuranceCompaniesManager = ({ token }) => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [formName, setFormName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchCompanies(); }, []);

  const fetchCompanies = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/superadmin/insurance-companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(res.data.companies || []);
    } catch (err) {
      toast.error('Failed to load insurance companies');
    } finally {
      setLoading(false);
    }
  };

  const openAdd = () => { setFormName(''); setEditCompany(null); setShowAddDialog(true); };
  const openEdit = (company) => { setFormName(company.name); setEditCompany(company); setShowAddDialog(true); };

  const handleSave = async () => {
    if (!formName.trim()) { toast.error('Company name is required'); return; }
    setSaving(true);
    try {
      if (editCompany) {
        await axios.put(
          `${API}/superadmin/insurance-companies/${editCompany.id}`,
          { name: formName.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Company updated');
      } else {
        await axios.post(
          `${API}/superadmin/insurance-companies`,
          { name: formName.trim() },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Company added');
      }
      setShowAddDialog(false);
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };


  const handleDelete = async (company) => {
    if (!window.confirm(`Delete "${company.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/superadmin/insurance-companies/${company.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Company deleted');
      fetchCompanies();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleToggle = async (company) => {
    try {
      await axios.put(
        `${API}/superadmin/insurance-companies/${company.id}`,
        { name: company.name, active: !company.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(company.active ? 'Company deactivated' : 'Company activated');
      fetchCompanies();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const filtered = companies.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row gap-3 mb-6 items-start sm:items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-secondary-800">Insurance Companies</h2>
          <p className="text-sm text-secondary-500 mt-0.5">
            These companies appear in the seller's vehicle upload form (Step 3 — Insurance & PUC).
          </p>
        </div>
        <Button onClick={openAdd} className="bg-orange-500 hover:bg-orange-600 text-white gap-2 flex-shrink-0">
          <Plus className="h-4 w-4" /> Add Company
        </Button>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <Search className="h-4 w-4 text-secondary-500" />
        <Input
          placeholder="Search companies..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-72"
        />
        <span className="text-sm text-secondary-500 ml-auto">{filtered.length} companies</span>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <Shield className="h-12 w-12 text-gray-200 mx-auto mb-3" />
          <p className="text-secondary-500 font-medium">
            {search ? 'No companies match your search' : 'No insurance companies added yet'}
          </p>
          {!search && (
            <Button onClick={openAdd} variant="outline" className="mt-4 gap-2">
              <Plus className="h-4 w-4" /> Add First Company
            </Button>
          )}
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((company) => (
            <div
              key={company.id}
              className={`flex items-center justify-between p-4 rounded-xl border transition-all ${
                company.active !== false
                  ? 'bg-white border-gray-200 hover:border-orange-200 hover:shadow-sm'
                  : 'bg-gray-50 border-gray-100 opacity-60'
              }`}
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  company.active !== false ? 'bg-blue-50' : 'bg-gray-100'
                }`}>
                  <Shield className={`h-4 w-4 ${company.active !== false ? 'text-blue-500' : 'text-gray-400'}`} />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-secondary-800 truncate">{company.name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <Badge
                      className={company.active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}
                      style={{ fontSize: '10px', padding: '1px 6px' }}
                    >
                      {company.active !== false ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                <button
                  onClick={() => openEdit(company)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-gray-500 hover:text-blue-600"
                  title="Edit"
                >
                  <Edit className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => handleToggle(company)}
                  className={`w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition text-xs font-bold ${
                    company.active !== false ? 'text-amber-500 hover:text-amber-700' : 'text-green-500 hover:text-green-700'
                  }`}
                  title={company.active !== false ? 'Deactivate' : 'Activate'}
                >
                  {company.active !== false ? '⏸' : '▶'}
                </button>
                <button
                  onClick={() => handleDelete(company)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 transition text-gray-400 hover:text-red-500"
                  title="Delete"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editCompany ? 'Edit Company' : 'Add Insurance Company'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm font-medium text-gray-700">Company Name *</Label>
              <Input
                className="mt-1"
                placeholder="e.g. Bajaj Allianz, HDFC ERGO..."
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                autoFocus
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button onClick={handleSave} disabled={saving} className="flex-1 bg-orange-500 hover:bg-orange-600 text-white">
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {editCompany ? 'Update' : 'Add Company'}
              </Button>
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="flex-1">
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [selectedCity, setSelectedCity] = useState('Belgaum');
  const [cities, setCities] = useState([]);
  const [pendingVehicles, setPendingVehicles] = useState([]);
  const [pendingAgents, setPendingAgents] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // ── FIX: state lives here in AdminDashboard, not inside InsuranceCompaniesManager ──
  const [inspectionSubmissionCount, setInspectionSubmissionCount] = useState(0);

  // Vehicle management state
  const [allVehicles, setAllVehicles] = useState([]);
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [vehicleSearch, setVehicleSearch] = useState('');
  const [vehicleStatusFilter, setVehicleStatusFilter] = useState('');

  // Agent details dialog
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showAgentDialog, setShowAgentDialog] = useState(false);
  const [blogCount, setBlogCount] = useState(0);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchCities();
    fetchPendingApprovals();
    fetchPendingAgents();
    fetchStatistics();
    fetchInspectionCount(); // ── FIX: called at AdminDashboard level
    fetchBlogCount();
  }, []);

  useEffect(() => {
    fetchAllVehicles();
  }, [vehicleFilter, vehicleStatusFilter, vehicleSearch]);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities`);
      setCities(res.data.cities);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  // (add inside useEffect or alongside fetchInspectionCount call)
  const fetchBlogCount = async () => {
    try {
      const res = await axios.get(`${API}/superadmin/blogs?limit=1&skip=0`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBlogCount(res.data.total || 0);
    } catch {}
  };

  const fetchPendingApprovals = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-approvals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingVehicles(res.data.vehicles);
    } catch (error) {
      console.error('Failed to fetch pending approvals:', error);
    }
  };

  const fetchPendingAgents = async () => {
    try {
      const res = await axios.get(`${API}/admin/pending-agents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingAgents(res.data.agents);
    } catch (error) {
      console.error('Failed to fetch pending agents:', error);
    }
  };

  const fetchStatistics = async () => {
    try {
      const res = await axios.get(`${API}/admin/statistics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(res.data);
    } catch (error) {
      console.error('Failed to fetch statistics:', error);
    }
  };

  // ── FIX: defined at AdminDashboard scope so inspectionSubmissionCount is accessible ──
  const fetchInspectionCount = async () => {
    try {
      const res = await axios.get(
        `${API}/admin/partner-inspection-submissions?skip=0&limit=1`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setInspectionSubmissionCount(res.data.total || 0);
    } catch {}
  };

  const fetchAllVehicles = async () => {
    try {
      const params = new URLSearchParams();
      if (vehicleFilter) params.set('vehicle_type', vehicleFilter);
      if (vehicleStatusFilter) params.set('status', vehicleStatusFilter);
      if (vehicleSearch) params.set('search', vehicleSearch);

      const res = await axios.get(`${API}/admin/vehicles?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllVehicles(res.data.vehicles);
    } catch (error) {
      console.error('Failed to fetch vehicles:', error);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        fetchPendingApprovals(),
        fetchPendingAgents(),
        fetchStatistics(),
        fetchAllVehicles(),
        fetchInspectionCount(),
        fetchBlogCount(),
      ]);
      toast.success('Dashboard refreshed');
    } catch {
      toast.error('Refresh failed');
    } finally {
      setRefreshing(false);
    }
  };

  const handleDeleteVehicle = async (vehicleId) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;
    try {
      await axios.delete(`${API}/admin/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Vehicle deleted');
      fetchAllVehicles();
      fetchStatistics();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete vehicle');
    }
  };

  const getVehicleIcon = (type) => {
    switch (type) {
      case 'car': return <Car className="h-4 w-4" />;
      case 'bike': return <Bike className="h-4 w-4" />;
      case 'ev': return <Zap className="h-4 w-4" />;
      case 'commercial': return <Truck className="h-4 w-4" />;
      default: return <Car className="h-4 w-4" />;
    }
  };

  const handleApproveVehicle = async (vehicleId) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/approve-vehicle`,
        { vehicle_id: vehicleId, status: 'approved' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vehicle approved!');
      fetchPendingApprovals();
      fetchStatistics();
    } catch (error) {
      toast.error('Failed to approve vehicle');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectVehicle = async (vehicleId) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/approve-vehicle`,
        { vehicle_id: vehicleId, status: 'rejected' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Vehicle rejected');
      fetchPendingApprovals();
    } catch (error) {
      toast.error('Failed to reject vehicle');
    } finally {
      setLoading(false);
    }
  };

  const viewAgentDetails = async (agent) => {
    try {
      const res = await axios.get(`${API}/admin/agents/${agent.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedAgent(res.data.agent);
      setShowAgentDialog(true);
    } catch (error) {
      toast.error('Failed to fetch agent details');
    }
  };

  const handleApproveAgent = async (agentId) => {
    setLoading(true);
    try {
      await axios.post(
        `${API}/admin/approve-agent?agent_id=${agentId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Agent approved!');
      fetchPendingAgents();
      setShowAgentDialog(false);
    } catch (error) {
      toast.error('Failed to approve agent');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary-50">
      <Header cities={cities} selectedCity={selectedCity} setSelectedCity={setSelectedCity} showSearch={false} />

      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-secondary-800 mb-2">Admin Dashboard</h1>
            <p className="text-secondary-600">Manage vehicle listings and approvals</p>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" onClick={handleRefresh} disabled={refreshing} className="gap-2" data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button onClick={() => navigate('/analytics')} className="gap-2" data-testid="view-analytics-btn">
              <BarChart3 className="h-4 w-4" />
              View Analytics
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Total Vehicles</p>
                  <p className="text-3xl font-bold text-secondary-800">{stats.total_vehicles || 0}</p>
                </div>
                <TrendingUp className="h-10 w-10 text-primary-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Pending Approvals</p>
                  <p className="text-3xl font-bold text-secondary-800">{stats.pending_approvals || 0}</p>
                </div>
                <Clock className="h-10 w-10 text-yellow-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Approved Vehicles</p>
                  <p className="text-3xl font-bold text-secondary-800">{stats.approved_vehicles || 0}</p>
                </div>
                <CheckCircle className="h-10 w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-secondary-600 mb-1">Pending Agents</p>
                  <p className="text-3xl font-bold text-secondary-800">{pendingAgents.length}</p>
                </div>
                <UserCheck className="h-10 w-10 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="vehicles">
          {/* ── FIX: ALL TabsTriggers are inside TabsList ── */}
          <TabsList className="mb-6 flex-wrap">
            <TabsTrigger value="vehicles" data-testid="vehicles-tab">
              Pending Vehicles ({pendingVehicles.length})
            </TabsTrigger>
            <TabsTrigger value="agents" data-testid="agents-tab">
              Pending Agents ({pendingAgents.length})
            </TabsTrigger>
            <TabsTrigger value="all-vehicles" data-testid="all-vehicles-tab">
              All Vehicles ({allVehicles.length})
            </TabsTrigger>
            <TabsTrigger value="unassigned-verifications">
              Unassigned Verifications
            </TabsTrigger>
            <TabsTrigger value="inspection-review">
              Partner Submissions ({inspectionSubmissionCount})
            </TabsTrigger>
            <TabsTrigger value="insurance-companies">
              Insurance Companies
            </TabsTrigger>
            <TabsTrigger value="blogs">
              Blogs ({blogCount})
            </TabsTrigger>
            <TabsTrigger value="bade-sahab">
              Master Data
            </TabsTrigger>
          </TabsList>

          {/* Pending Vehicles */}
          <TabsContent value="vehicles">
            <Card>
              <CardHeader><CardTitle>Pending Vehicle Approvals</CardTitle></CardHeader>
              <CardContent>
                {pendingVehicles.length === 0 ? (
                  <p className="text-center text-secondary-600 py-8">No pending approvals</p>
                ) : (
                  <div className="space-y-4">
                    {pendingVehicles.map((vehicle, idx) => (
                      <div key={vehicle.id} className="flex items-center justify-between p-4 border rounded-lg" data-testid={`pending-vehicle-${idx}`}>
                        <div className="flex items-center gap-4">
                          <img src={vehicle.images?.[0] || 'https://via.placeholder.com/100'} alt={vehicle.brand_name} className="w-24 h-24 object-cover rounded-lg" />
                          <div>
                            <h3 className="font-semibold text-secondary-800">
                              {vehicle.brand_name || 'Unknown'} {vehicle.model_name || ''}
                            </h3>
                            <p className="text-sm text-secondary-600">{vehicle.registration_no}</p>
                            {vehicle.vehicle_display_id && (
                              <p className="text-xs font-mono text-blue-600 mt-0.5">{vehicle.vehicle_display_id}</p>
                            )}
                            <p className="text-sm text-secondary-600">{vehicle.city}</p>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {vehicle.has_insurance === true && (
                                <span className="text-[10px] bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-medium">
                                  🛡️ Insured · {vehicle.insurance_company}
                                </span>
                              )}
                              {vehicle.has_insurance === false && (
                                <span className="text-[10px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">No Insurance</span>
                              )}
                              {vehicle.has_puc === true && (
                                <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">✅ PUC Valid</span>
                              )}
                              {vehicle.has_puc === false && (
                                <span className="text-[10px] bg-red-100 text-red-600 px-2 py-0.5 rounded-full">No PUC</span>
                              )}
                            </div>
                            <div className="flex gap-2 mt-1">
                              <Badge className="bg-primary-100 text-primary-700">{vehicle.price}</Badge>
                              {vehicle.payment_id && <Badge variant="outline" className="text-xs">{vehicle.payment_id}</Badge>}
                              {vehicle.payment_status === 'paid' ? (
                                <Badge className="bg-green-100 text-green-700">Payment Done ✅</Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700">Payment Pending ❌</Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => handleApproveVehicle(vehicle.id)} disabled={loading || vehicle.payment_status !== 'paid'} className="bg-green-500 hover:bg-green-600" data-testid={`approve-vehicle-btn-${idx}`}>
                            <CheckCircle className="mr-1 h-4 w-4" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleRejectVehicle(vehicle.id)} disabled={loading} data-testid={`reject-vehicle-btn-${idx}`}>
                            <XCircle className="mr-1 h-4 w-4" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pending Agents */}
          <TabsContent value="agents">
            <Card>
              <CardHeader><CardTitle>Pending Agent Approvals</CardTitle></CardHeader>
              <CardContent>
                {pendingAgents.length === 0 ? (
                  <p className="text-center text-secondary-600 py-8">No pending agent approvals</p>
                ) : (
                  <div className="space-y-4">
                    {pendingAgents.map((agent, idx) => (
                      <div key={agent.id} className="flex items-center justify-between p-4 border rounded-lg cursor-pointer hover:bg-secondary-50" onClick={() => viewAgentDetails(agent)} data-testid={`pending-agent-${idx}`}>
                        <div>
                          <h3 className="font-semibold text-secondary-800">{agent.name}</h3>
                          <p className="text-sm text-secondary-600">{agent.email}</p>
                          <p className="text-sm text-secondary-600">{agent.phone}</p>
                          <p className="text-sm text-secondary-600">City: {agent.city}</p>
                          {agent.documents && agent.documents.length > 0 && (
                            <Badge variant="outline" className="mt-1">{agent.documents.length} document(s) uploaded</Badge>
                          )}
                        </div>
                        <Button variant="outline" onClick={(e) => { e.stopPropagation(); viewAgentDetails(agent); }} data-testid={`view-agent-btn-${idx}`}>
                          <Eye className="mr-1 h-4 w-4" /> View Details
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* All Vehicles */}
          <TabsContent value="all-vehicles">
            <Card>
              <CardHeader><CardTitle>All Vehicle Listings</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-4 mb-6">
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-secondary-500" />
                    <Input placeholder="Search (case-sensitive)..." value={vehicleSearch} onChange={(e) => setVehicleSearch(e.target.value)} className="w-48" data-testid="vehicle-search-input" />
                  </div>
                  <Select value={vehicleFilter || "all"} onValueChange={(val) => setVehicleFilter(val === "all" ? "" : val)}>
                    <SelectTrigger className="w-40" data-testid="vehicle-type-filter"><SelectValue placeholder="Vehicle Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="car">Cars</SelectItem>
                      <SelectItem value="bike">Bikes</SelectItem>
                      <SelectItem value="ev">EV</SelectItem>
                      <SelectItem value="commercial">Commercial</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={vehicleStatusFilter || "all"} onValueChange={(val) => setVehicleStatusFilter(val === "all" ? "" : val)}>
                    <SelectTrigger className="w-40" data-testid="vehicle-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {allVehicles.length === 0 ? (
                  <p className="text-center text-secondary-600 py-8">No vehicles found</p>
                ) : (
                  <div className="space-y-3">
                    {allVehicles.map((vehicle, idx) => (
                      <div key={vehicle.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-secondary-50" data-testid={`all-vehicle-${idx}`}>
                        <div className="flex items-center gap-4">
                          <img src={vehicle.images?.[0] || 'https://via.placeholder.com/100'} alt={vehicle.brand_name} className="w-16 h-16 object-cover rounded-lg" />
                          <div>
                            <div className="flex items-center gap-2">
                              {getVehicleIcon(vehicle.vehicle_type)}
                              <h3 className="font-semibold text-secondary-800">{vehicle.brand_name} {vehicle.model_name}</h3>
                            </div>
                            <p className="text-sm text-secondary-600">{vehicle.registration_no} | {vehicle.city}</p>
                            {vehicle.vehicle_display_id && (
                              <p className="text-xs font-mono text-blue-600 mt-0.5">{vehicle.vehicle_display_id}</p>
                            )}
                            <div className="flex gap-1 mt-1">
                              {vehicle.has_insurance === true && (
                                <span className="text-[10px] bg-blue-50 text-blue-600 border border-blue-100 px-1.5 py-0.5 rounded">
                                  🛡️ {vehicle.insurance_company || 'Insured'}
                                </span>
                              )}
                              {vehicle.has_puc === true && (
                                <span className="text-[10px] bg-green-50 text-green-600 border border-green-100 px-1.5 py-0.5 rounded">✅ PUC</span>
                              )}
                            </div>
                            <div className="flex gap-2 mt-1">
                              <Badge className="bg-primary-100 text-primary-700">{vehicle.price}</Badge>
                              <Badge className={
                                vehicle.status === 'approved' ? 'bg-green-100 text-green-700' :
                                vehicle.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-red-100 text-red-700'
                              }>{vehicle.status}</Badge>
                              {vehicle.verified_by_x7autoz && (
                                <span className="inline-flex items-center gap-1 bg-blue-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md">
                                  <svg viewBox="0 0 24 24" className="w-3 h-3 fill-white flex-shrink-0">
                                    <rect x="2" y="2" width="20" height="20" rx="4" />
                                    <path d="M7 12.5l3.5 3.5 6.5-7" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                                  </svg>
                                  VERIFIED
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => navigate(`/vehicles/${vehicle.id}`)}><Eye className="h-4 w-4" /></Button>
                          <Button variant="outline" size="sm" onClick={() => navigate(`/edit-vehicle/${vehicle.id}`)}><Edit className="h-4 w-4" /></Button>
                          <Button variant="destructive" size="sm" onClick={() => handleDeleteVehicle(vehicle.id)}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="unassigned-verifications">
            <AdminUnassignedVerifications />
          </TabsContent>

          <TabsContent value="inspection-review">
            <AdminVerificationReview />
          </TabsContent>

          <TabsContent value="insurance-companies">
            <Card>
              <CardContent className="p-6">
                <InsuranceCompaniesManager token={token} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="blogs">
            <Card>
              <CardContent className="p-6">
                <BlogsManager token={token} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bade-sahab">
            <BadeSahabData />
          </TabsContent>
        </Tabs>
      </div>

      {/* Agent Details Dialog */}
      <Dialog open={showAgentDialog} onOpenChange={setShowAgentDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Agent Application Details</DialogTitle></DialogHeader>
          {selectedAgent && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-secondary-600">Name</Label><p className="font-medium">{selectedAgent.name}</p></div>
                <div><Label className="text-secondary-600">Email</Label><p className="font-medium">{selectedAgent.email}</p></div>
                <div><Label className="text-secondary-600">Phone</Label><p className="font-medium">{selectedAgent.phone}</p></div>
                <div><Label className="text-secondary-600">City</Label><p className="font-medium">{selectedAgent.city}</p></div>
                <div><Label className="text-secondary-600">Business Name</Label><p className="font-medium">{selectedAgent.business_name || 'N/A'}</p></div>
                <div><Label className="text-secondary-600">GST Number</Label><p className="font-medium">{selectedAgent.gst_number || 'N/A'}</p></div>
              </div>
              {selectedAgent.documents && selectedAgent.documents.length > 0 && (
                <div>
                  <Label className="text-secondary-600 mb-2 block">Uploaded Documents</Label>
                  <div className="grid grid-cols-2 gap-4">
                    {selectedAgent.documents.map((doc, idx) => (
                      <div key={idx} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <FileText className="h-5 w-5 text-secondary-500" />
                            <span className="text-sm font-medium">{doc.name || `Document ${idx + 1}`}</span>
                          </div>
                          <a href={doc.url || doc} target="_blank" rel="noopener noreferrer" className="text-primary-500 hover:text-primary-600">
                            <ExternalLink className="h-4 w-4" />
                          </a>
                        </div>
                        {(doc.url || doc).startsWith('data:image') && (
                          <img src={doc.url || doc} alt={`Document ${idx + 1}`} className="mt-2 w-full h-32 object-cover rounded" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 pt-4">
                <Button onClick={() => handleApproveAgent(selectedAgent.id)} disabled={loading} className="flex-1 bg-green-500 hover:bg-green-600">
                  <CheckCircle className="mr-2 h-4 w-4" />Approve Agent
                </Button>
                <Button variant="outline" onClick={() => setShowAgentDialog(false)} className="flex-1">Close</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default AdminDashboard;